import { DocumentNode, gql, useMutation } from '@apollo/client';
import { useBoolean } from '@chakra-ui/react';
import * as React from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { EditableCard } from '@/components/EditableCard';
import { EDigestPreference } from '@/graphql-types/globalTypes';
import { usePermissions } from '@/hooks/usePermissions';
import { logError } from '@/imports/logging/ClientLogger';

import { DetailsCard_GroupFragment } from './graphql-types/DetailsCard_GroupFragment';
import {
  DetailsCard_UpdateGroup,
  DetailsCard_UpdateGroupVariables,
} from './graphql-types/DetailsCard_UpdateGroup';

import { DetailsForm } from '../../../DetailsForm';

const UPDATE_GROUP = gql`
  mutation DetailsCard_UpdateGroup(
    $input: UpdateGroupInput!
    $subgroupsInput: SubgroupsInput
  ) {
    updateGroup(input: $input) {
      data {
        _id
        name
        description
        isGlobalSingleton
        priority
        parentGroup {
          _id
          subgroups(input: $subgroupsInput) {
            groupNodes {
              group {
                _id
              }
            }
          }
        }
      }
    }
  }
`;

type IDetailsCardForm = {
  name: string;
  description?: string;
  priority: number;
  defaultDigestPref: EDigestPreference;
};

export const DetailsCard: React.FC<{
  group: DetailsCard_GroupFragment;
}> & { groupFragment: DocumentNode } = ({ group }) => {
  const permissions = usePermissions();
  const [isEditing, setIsEditing] = useBoolean();

  const [updateGroup, { loading }] = useMutation<
    DetailsCard_UpdateGroup,
    DetailsCard_UpdateGroupVariables
  >(UPDATE_GROUP);

  const defaultValues = {
    name: group.name,
    description: group.description ?? undefined,
    priority: group.priority,
    defaultDigestPref: group.settings.defaultDigestPref,
  };

  const methods = useForm<IDetailsCardForm>({ defaultValues });
  const { handleSubmit, reset } = methods;

  return (
    <FormProvider {...methods}>
      <EditableCard
        title={!!group.parentGroup?._id ? 'Subgroup Details' : 'Group Details'}
        onSubmit={handleSubmit(async (data) => {
          try {
            await updateGroup({
              variables: {
                input: {
                  id: group._id,
                  data: {
                    name: data.name,
                    description: data.description,
                    settings: { defaultDigestPref: data.defaultDigestPref },
                    priority: data.priority,
                  },
                },
                subgroupsInput: {
                  filter: permissions.canDoAdminStuffAndAuthenticated
                    ? { includeAll: true }
                    : undefined,
                },
              },
            });
          } catch (e) {
            logError(e);
          } finally {
            setIsEditing.off();
          }
        })}
        onClickEdit={setIsEditing.on}
        onClickCancel={() => {
          setIsEditing.off();
          reset();
        }}
        isEditing={isEditing}
        isCancelDisabled={loading}
        isSubmitLoading={loading}
        w="full"
      >
        <DetailsForm
          isGlobalSingleton={!!group.isGlobalSingleton}
          isEditing={isEditing}
          isNewGroup={false}
          parentGroupId={group.parentGroup?._id}
        />
      </EditableCard>
    </FormProvider>
  );
};
DetailsCard.groupFragment = gql`
  fragment DetailsCard_GroupFragment on Group {
    _id
    isGlobalSingleton
    name
    description
    priority
    settings {
      defaultDigestPref
    }
    parentGroup {
      _id
    }
  }
`;
